import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import styles from './css/About.module.css';

const fadeUp = {
  hidden:  { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const SKILLS = [
  {
    icon: '🌐',
    title: 'Web Design & Development',
    desc: 'From pixel-perfect UI design to production-ready React applications. Building fast, accessible, responsive web experiences that users actually enjoy.',
  },
  {
    icon: '📱',
    title: 'Mobile App Development',
    desc: 'Cross-platform iOS and Android apps using React Native. Native feel, shared codebase, deployed to both stores.',
  },
  {
    icon: '⚙️',
    title: '.NET & Enterprise Apps',
    desc: 'Deep experience with the .NET ecosystem — building robust, maintainable desktop and enterprise applications with C# and the broader Microsoft stack.',
  },
  {
    icon: '🔗',
    title: 'API Development & Integration',
    desc: 'Designing and building RESTful and GraphQL APIs, integrating third-party services, and connecting disparate systems into coherent platforms.',
  },
  {
    icon: '🗄️',
    title: 'Database Design & Management',
    desc: 'Relational and NoSQL database architecture — from optimised schemas and indexing strategies to migrations, performance tuning, and data integrity.',
  },
  {
    icon: '🔧',
    title: 'Full-Stack Development',
    desc: 'Comfortable across the entire stack — frontend, backend, infrastructure, and everything in between. No handoff needed.',
  },
  {
    icon: '☁️',
    title: 'Cloud & DevOps',
    desc: 'Deployment pipelines, containerisation with Docker, and cloud hosting on AWS, Azure, and GCP. Reliable, scalable, automated.',
  },
  {
    icon: '🛡️',
    title: 'Security & Best Practices',
    desc: 'Security-conscious development by default. Authentication, authorisation, input validation, and OWASP standards baked in from day one.',
  },
];

const PHILOSOPHY = [
  { icon: '✏️', title: 'Code that communicates', text: 'Clean, readable code is not a luxury — it\'s a professional standard. Future maintainers (including yourself) will thank you.' },
  { icon: '🎯', title: 'Pragmatic, not dogmatic', text: 'The right tool for the right job. No religion about frameworks — just what solves the problem well and stands the test of time.' },
  { icon: '🔄', title: 'Continuous improvement', text: 'Thirty years in the industry means knowing how much there is still to learn. Curiosity and adaptability are non-negotiable.' },
  { icon: '🤝', title: 'Honest collaboration', text: 'Good software starts with good communication. Understanding what someone actually needs is half the battle won.' },
];

const About = () => {
  return (
    <div className={styles.page}>

      {/* ── Header ── */}
      <motion.div
        className={styles.pageHeader}
        initial="hidden"
        animate="visible"
        variants={fadeUp}
        transition={{ duration: 0.7 }}
      >
        <span className={styles.sectionLabel}>Who We Are</span>
        <div className={styles.divider} />
        <h1 className={styles.pageTitle}>About Byte79 Ltd</h1>
        <p className={styles.pageSubtitle}>
          A software development company built on three decades of real-world experience —
          writing code that works, ships on time, and lasts.
        </p>
      </motion.div>

      {/* ── Intro ── */}
      <div className={styles.introLayout}>

        {/* Identity card */}
        <motion.div
          className={styles.identityCard}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          <div className={styles.avatarRing}>👨‍💻</div>
          <h2 className={styles.identityName}>Andrew Thorpe</h2>
          <p className={styles.identityRole}>Founder & Lead Developer</p>
          <p className={styles.identityCompany}>Byte79 Ltd</p>

          <div className={styles.statRow}>
            <div className={styles.stat}>
              <span className={styles.statNum}>30+</span>
              <span className={styles.statLabel}>Years in IT</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statNum}>Full</span>
              <span className={styles.statLabel}>Stack</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statNum}>∞</span>
              <span className={styles.statLabel}>Coffee</span>
            </div>
          </div>
        </motion.div>

        {/* Bio */}
        <motion.div
          className={styles.bioCol}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7, delay: 0.18 }}
        >
          <p className={styles.bioText}>
            I've been writing software professionally for <strong>over 30 years</strong> — long enough to have seen plenty of trends come and go, and to know which ones actually matter. That perspective shapes everything I build.
          </p>
          <p className={styles.bioText}>
            My background spans the full breadth of software development: <em>web design & development</em>, <em>mobile apps</em>, <em>.NET & enterprise applications</em>, <em>API development & integration</em>, <em>database design</em>, and everything in between. I'm as comfortable planning an architecture as I am writing the code that implements it.
          </p>
          <p className={styles.bioText}>
            <strong>Byte79 Ltd</strong> was founded on a simple belief: that good software should be well-crafted, honestly delivered, and built to last. Not patched together under pressure to hit a deadline, and not over-engineered to justify a fee — just the right solution, done properly.
          </p>
          <p className={styles.bioText}>
            Whether you need a brand-new product built from scratch, an existing system modernised, or a technical partner who can cut through the noise and tell you what you actually need — that's what I'm here for.
          </p>
        </motion.div>

      </div>

      {/* ── Skills ── */}
      <motion.div
        className={styles.skillsSection}
        initial="hidden"
        whileInView="visible"
        variants={fadeUp}
        transition={{ duration: 0.6 }}
        viewport={{ once: true, amount: 0.1 }}
      >
        <div className={styles.sectionHeader}>
          <span className={styles.sectionLabel}>Expertise</span>
          <div className={styles.divider} />
          <h2 className={styles.sectionTitle}>Skills & Specialisms</h2>
          <p className={styles.sectionSubtitle}>
            Three decades of breadth, with genuine depth in the areas that matter most for modern software.
          </p>
        </div>

        <div className={styles.skillsGrid}>
          {SKILLS.map((s, i) => (
            <motion.div
              key={i}
              className={styles.skillCard}
              initial="hidden"
              whileInView="visible"
              variants={fadeUp}
              transition={{ duration: 0.45, delay: i * 0.07 }}
              viewport={{ once: true, amount: 0.1 }}
            >
              <span className={styles.skillCardIcon}>{s.icon}</span>
              <h3 className={styles.skillCardTitle}>{s.title}</h3>
              <p className={styles.skillCardDesc}>{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* ── Philosophy ── */}
      <section className={styles.philosophySection}>
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, amount: 0.1 }}
        >
          <div className={styles.sectionHeader}>
            <span className={styles.sectionLabel}>How I Work</span>
            <div className={styles.divider} />
            <h2 className={styles.sectionTitle}>Philosophy</h2>
            <p className={styles.sectionSubtitle}>
              Principles that have held up across 30 years of building software for real people.
            </p>
          </div>
        </motion.div>

        <div className={styles.philosophyGrid}>
          {PHILOSOPHY.map((p, i) => (
            <motion.div
              key={i}
              className={styles.philosophyItem}
              initial="hidden"
              whileInView="visible"
              variants={fadeUp}
              transition={{ duration: 0.45, delay: i * 0.08 }}
              viewport={{ once: true, amount: 0.1 }}
            >
              <span className={styles.philosophyIcon}>{p.icon}</span>
              <h3 className={styles.philosophyTitle}>{p.title}</h3>
              <p className={styles.philosophyText}>{p.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <motion.div
        className={styles.ctaStrip}
        initial="hidden"
        whileInView="visible"
        variants={fadeUp}
        transition={{ duration: 0.7 }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <h2 className={styles.ctaTitle}>Let's work together</h2>
        <p className={styles.ctaText}>
          Have a project in mind? Get in touch and let's talk through what you need.
        </p>
        <div className={styles.ctaButtons}>
          <Link to="/contact"  className={styles.btnPrimary}>Start a Conversation →</Link>
          <Link to="/services" className={styles.btnSecondary}>View Services</Link>
        </div>
      </motion.div>

    </div>
  );
};

export default About;

